// ============================================================
// targetEdits - the Target editor's writes, as pure functions
// ============================================================
// Every edit returns a NEW target (deep-cloned), never touching the one it
// was handed: the roster's projections are memoized on object identity.

import deepClone from '../utils/deepClone';
import type { Target } from '../types';
import { defaultArmourForBody } from './bodyConfig';
import { bodyTypeFor, isCrowd, objectTakes, type BodyShape } from './bodyShape';

/**
 * A copy of `obj` with the dotted `path` set to `val`, creating any
 * missing object on the way ('armour.head', 'horde.magnitude').
 */
export function withPath<T extends object>(obj: T, path: string, val: unknown): T {
  const next = deepClone(obj);
  const keys = path.split('.');
  let node = next as Record<string, unknown>;
  for (const key of keys.slice(0, -1)) {
    if (!node[key]) node[key] = {};
    node = node[key] as Record<string, unknown>;
  }
  node[keys[keys.length - 1]] = val;
  return next;
}

/** The body type as stored, reading a legacy horde flag as `horde`. */
function storedBodyType(target: Target): string {
  return target.bodyType || (target.horde?.isHorde ? 'horde' : 'humanoid');
}

/** Would this shape and crowd-ness leave the target exactly as it is? */
export function isSameBody(target: Target, shape: BodyShape, crowd: boolean): boolean {
  return bodyTypeFor(shape, crowd) === storedBodyType(target) && crowd === isCrowd(target);
}

/**
 * The question to ask before a body switch, or null when there is nothing
 * to lose: the switch resets armour, and strips location conditions.
 */
export function bodySwitchWarning(target: Target): string | null {
  const hasDamage = target.wounds.current !== target.wounds.max;
  const hasConds = (target.conditions || []).length > 0;
  if (!hasDamage && !hasConds) return null;
  const conds = hasConds ? ' and remove location-specific conditions' : '';
  return `Switching body type will reset armour${conds}. Continue?`;
}

const VEHICLE_CONDS = ['Immob', 'WepDest', 'EngDmg', 'HullBr', 'CrewHit', 'Disabled', 'Explodes'];

/**
 * Keyed on the SHAPE: a humanoid that becomes a mob is still shaped like
 * a humanoid, so its location crits still mean something.
 */
export function keepsCondition(code: string, shape: BodyShape): boolean {
  // An object keeps only what it takes (2026-10-26): its destruction.
  if (shape === 'object') return objectTakes(code);
  if (shape !== 'humanoid' && /^(Cr|De):/.test(code)) return false;
  return shape === 'vehicle' || !VEHICLE_CONDS.includes(code);
}

/**
 * The target re-bodied: the stored type for the shape and crowd-ness, the
 * body's default armour, the horde flag, a vehicle's facing back to Auto,
 * and the conditions the new shape cannot carry removed.
 */
export function rebodied(target: Target, shape: BodyShape, crowd: boolean): Target {
  const next = deepClone(target);
  next.bodyType = bodyTypeFor(shape, crowd);
  next.armour = defaultArmourForBody(next.bodyType);
  next.horde = { ...next.horde, isHorde: crowd };
  next.vehicleFacing = shape === 'vehicle' ? 'auto' : null;
  next.conditions = (next.conditions || []).filter((c) => keepsCondition(c, shape));
  return next;
}
